import _ from 'lodash';
import {ImageAttributes} from '@treez/commons/sharedTypings';
import {PriceTier} from '@treez/commons/sharedTypings/priceTier';
import {
    ContactInformationAttributes,
    Entity,
    Organization,
} from '@treez/commons/sharedTypings/directory';
import {OwnerType} from '@treez/commons/sharedTypings/product';
import {Dispatch} from 'react';
import {SnackbarType} from '@treez-ui/core/feedback/modals';
import {getAllOrgCatalogs, getOrgPriceTiers} from '~/utils/ProductAPI';
import {getOrganization, getStoreContactInformation, getStores} from '~/utils/DirectoryAPI';
import {StoreAttributes} from './directory';
import {clearSnackbar, showSnackbar} from '~/redux/actions/SnackbarActions';

export interface EntityWithCatalog extends Entity {
    catalogId: number;
}

export interface OrganizationWithCatalogsAttributesAndPriceTiers extends Organization {
    catalogId: number;
    priceTiers: PriceTier[];
}

export const getPrimaryContactInformation = (
    contactInformation: ContactInformationAttributes[]
) => {
    const primaryContactInformation =
        _.find(contactInformation, {primary: true}) || contactInformation[0];

    return primaryContactInformation;
};

export const getAddressString = (
    contactInformation: ContactInformationAttributes[] | undefined
) => {
    if (contactInformation == null || _.size(contactInformation) < 1) {
        return '';
    }

    const primaryContactInformation = getPrimaryContactInformation(contactInformation);

    const {addressLineOne, city, postalCode, state} = primaryContactInformation;

    let cityToAddress;

    if (city) {
        cityToAddress = `${city},`;
    }

    return [addressLineOne, cityToAddress, state, postalCode].filter((field) => field).join(' ');
};

export const getPrimaryImageUrl = (images: ImageAttributes[] = []) => {
    if (_.size(images) === 0) {
        return 'https://assets.treez.io/bg/default_store_thumbnail.svg';
    }
    const defaultImage = _.find<ImageAttributes>(images, {default: true});
    const primaryImage = defaultImage || images[0];

    return primaryImage.url;
};

export const getStoreRegion = (store: StoreAttributes | undefined): string | undefined => {
    if (store && store.contactInformation) {
        const primaryContactInformation = getPrimaryContactInformation(store.contactInformation);

        return primaryContactInformation?.state;
    }
};

export const searchForOrganization = async (
    organizationTypeId: number,
    dispatch: Dispatch<any>
): Promise<OrganizationWithCatalogsAttributesAndPriceTiers> => {
    const organization = await getOrganization(organizationTypeId);
    const catalogResponse = await getAllOrgCatalogs(organization.id);

    const {data: catalogs} = catalogResponse;

    const organizationalCatalog = catalogs.data[0];

    const childCatalogs = _.get(organizationalCatalog, 'childCatalogs');

    const stores = await getStores(organization.id);

    const storeContactInformation = await Promise.all(
        stores.map(async (store) => {
            const contactInformation = await getStoreContactInformation(store.typeId);

            return {
                id: store.id,
                contactInfo: contactInformation,
            };
        })
    ).then(
        (storeContactInfo) =>
            new Map<number, ContactInformationAttributes[]>(
                storeContactInfo.map((storeContact) => [storeContact.id, storeContact.contactInfo])
            )
    );

    let storesWithCatalogs: StoreAttributes[] = [];

    if (_.size(stores) > 0) {
        storesWithCatalogs = stores
            .filter((store) => {
                const childCatalog = childCatalogs.find(
                    (catalog) =>
                        catalog.ownerType === OwnerType.STORE && catalog.ownerId === store.id
                );
                if (!childCatalog) {
                    dispatch(
                        showSnackbar({
                            onClose: () => dispatch(clearSnackbar()),
                            type: SnackbarType.WARNING,
                            content: `Store catalog is missing for ${store.name}, please contact support@treez.io`,
                        })
                    );
                    console.error(
                        `Store catalog is missing for ${store.name}, please contact support@treez.io`
                    );
                    return false;
                }

                return true;
            })
            .map((store) => {
                const {id: catalogId} = _.find(childCatalogs, {
                    ownerType: OwnerType.STORE,
                    ownerId: store.id,
                });

                const contactInformation = storeContactInformation.get(store.id);

                return {
                    ...store,
                    contactInformation,
                    catalogId,
                };
            });
    }

    const priceTiers = await getOrgPriceTiers(organizationalCatalog.id);

    const organizationWithCatalogs = {
        ...organization,
        catalogId: organizationalCatalog.id,
        stores: storesWithCatalogs,
        priceTiers,
    };

    return organizationWithCatalogs;
};
